import React, { useMemo, useState } from "react";
import { toast } from "sonner";
import { ArrowRight, Mail, MessageCircle, MapPin } from "lucide-react";
import {
    WHATSAPP_URL,
    WHATSAPP_NUMBER,
    CONTACT_EMAIL,
    SERVICES,
    PACKAGES,
    MAPS_URL,
    ADDRESS,
} from "../data/site";

const GOALS = [
    "Crescita muscolare",
    "Definizione / dimagrimento",
    "Forza",
    "Skill calisthenics",
    "Solo nutrizione",
    "Altro",
];

const buildWhatsAppMessage = (form) => {
    const lines = [
        "*Nuova richiesta RPF Coaching*",
        "",
        `*Nome*: ${form.name || "-"}`,
        `*Email*: ${form.email || "-"}`,
    ];
    if (form.phone) lines.push(`*Telefono*: ${form.phone}`);
    if (form.service) lines.push(`*Servizio/Pacchetto*: ${form.service}`);
    if (form.goal) lines.push(`*Obiettivo*: ${form.goal}`);
    lines.push("", `*Messaggio*:`, form.message || "-");
    return lines.join("\n");
};

export default function Contact() {
    const [form, setForm] = useState({
        name: "",
        email: "",
        phone: "",
        service: "",
        goal: "",
        message: "",
    });

    const update = (field) => (e) =>
        setForm((f) => ({ ...f, [field]: e.target.value }));

    // Link WhatsApp dinamico: cambia ad ogni keystroke
    const waLink = useMemo(() => {
        const text = buildWhatsAppMessage(form);
        return `${WHATSAPP_URL}?text=${encodeURIComponent(text)}`;
    }, [form]);

    const handleClick = (e) => {
        // Validazione minima
        if (!form.name || !form.email || !form.message) {
            e.preventDefault();
            toast.error("Compila nome, email e messaggio.");
            return;
        }

        toast.success("Apriamo WhatsApp con la tua richiesta. Premi invio per confermare!");

        // Reset form leggermente dopo, così il navigate non viene cancellato
        setTimeout(() => {
            setForm({
                name: "",
                email: "",
                phone: "",
                service: "",
                goal: "",
                message: "",
            });
        }, 800);
    };

    return (
        <section
            id="contatti"
            data-testid="contact-section"
            className="relative py-24 sm:py-32 bg-ink-surface/40 border-t border-white/5 overflow-hidden"
        >
            <div className="absolute -top-20 -right-20 w-[400px] h-[400px] rounded-full bg-gold/5 blur-3xl" />
            <div className="absolute bottom-0 left-0 w-full h-32 bg-grid opacity-40" />

            <div className="container mx-auto px-4 sm:px-6 lg:px-10 relative">
                <div className="grid lg:grid-cols-12 gap-12 lg:gap-16">
                    {/* Left: copy */}
                    <div className="lg:col-span-5">
                        <div className="font-manrope text-sm uppercase tracking-[0.3em] text-gold mb-4">
                            Contattaci
                        </div>
                        <h2
                            data-testid="contact-title"
                            className="font-oswald font-bold uppercase text-4xl sm:text-5xl lg:text-6xl text-white leading-[1]"
                        >
                            Pronto a
                            <br />
                            <span className="text-shimmer">fare sul serio?</span>
                        </h2>
                        <p className="mt-6 font-manrope text-zinc-400 text-base sm:text-lg">
                            Compila il modulo: la tua richiesta arriverà direttamente su WhatsApp
                            al coach Manuel. Riceverai risposta personalmente.
                        </p>

                        <div className="mt-10 space-y-4">
                            <a
                                href={WHATSAPP_URL}
                                target="_blank"
                                rel="noreferrer"
                                data-testid="contact-whatsapp"
                                className="flex items-center gap-4 p-5 rounded-xl bg-ink border border-white/5 hover:border-gold/50 group transition-all"
                            >
                                <div className="w-11 h-11 rounded-lg bg-gold/10 border border-gold/30 flex items-center justify-center group-hover:bg-gold group-hover:border-gold transition-colors">
                                    <MessageCircle
                                        size={20}
                                        className="text-gold group-hover:text-ink transition-colors"
                                    />
                                </div>
                                <div>
                                    <div className="font-manrope text-xs uppercase tracking-[0.25em] text-zinc-500">
                                        WhatsApp
                                    </div>
                                    <div className="font-oswald font-bold text-white text-lg tracking-wide">
                                        {WHATSAPP_NUMBER}
                                    </div>
                                </div>
                            </a>

                            <a
                                href={`mailto:${CONTACT_EMAIL}`}
                                data-testid="contact-email-link"
                                className="flex items-center gap-4 p-5 rounded-xl bg-ink border border-white/5 hover:border-gold/50 group transition-all"
                            >
                                <div className="w-11 h-11 rounded-lg bg-gold/10 border border-gold/30 flex items-center justify-center group-hover:bg-gold group-hover:border-gold transition-colors">
                                    <Mail
                                        size={20}
                                        className="text-gold group-hover:text-ink transition-colors"
                                    />
                                </div>
                                <div>
                                    <div className="font-manrope text-xs uppercase tracking-[0.25em] text-zinc-500">
                                        Email
                                    </div>
                                    <div className="font-oswald font-bold text-white text-lg tracking-wide break-all">
                                        {CONTACT_EMAIL}
                                    </div>
                                </div>
                            </a>

                            <a
                                href={MAPS_URL}
                                target="_blank"
                                rel="noopener"
                                data-testid="contact-maps-link"
                                className="flex items-center gap-4 p-5 rounded-xl bg-ink border border-white/5 hover:border-gold/50 group transition-all"
                            >
                                <div className="w-11 h-11 rounded-lg bg-gold/10 border border-gold/30 flex items-center justify-center group-hover:bg-gold group-hover:border-gold transition-colors">
                                    <MapPin
                                        size={20}
                                        className="text-gold group-hover:text-ink transition-colors"
                                    />
                                </div>
                                <div className="min-w-0">
                                    <div className="font-manrope text-xs uppercase tracking-[0.25em] text-zinc-500">
                                        Sede — Apri su Google Maps
                                    </div>
                                    <div className="font-oswald font-bold text-white text-base sm:text-lg tracking-wide truncate">
                                        {ADDRESS}
                                    </div>
                                </div>
                            </a>
                        </div>
                    </div>

                    {/* Right: form */}
                    <div className="lg:col-span-7">
                        <form
                            onSubmit={(e) => e.preventDefault()}
                            data-testid="contact-form"
                            className="bg-ink border border-white/10 rounded-2xl p-6 sm:p-10"
                        >
                            <div className="grid sm:grid-cols-2 gap-5">
                                <Field label="Nome e cognome *">
                                    <input
                                        type="text"
                                        required
                                        data-testid="contact-input-name"
                                        value={form.name}
                                        onChange={update("name")}
                                        placeholder="Mario Rossi"
                                        className="input"
                                    />
                                </Field>
                                <Field label="Email *">
                                    <input
                                        type="email"
                                        required
                                        data-testid="contact-input-email"
                                        value={form.email}
                                        onChange={update("email")}
                                        placeholder="mario@email.com"
                                        className="input"
                                    />
                                </Field>
                                <Field label="Telefono">
                                    <input
                                        type="tel"
                                        data-testid="contact-input-phone"
                                        value={form.phone}
                                        onChange={update("phone")}
                                        placeholder="+39 333 ..."
                                        className="input"
                                    />
                                </Field>
                                <Field label="Pacchetto / Servizio">
                                    <select
                                        data-testid="contact-select-service"
                                        value={form.service}
                                        onChange={update("service")}
                                        className="input"
                                    >
                                        <option value="">Seleziona...</option>
                                        <optgroup label="Pacchetti">
                                            {PACKAGES.map((p) => (
                                                <option
                                                    key={p.id}
                                                    value={`${p.name} (€${p.price})`}
                                                >
                                                    {p.name} — €{p.price}
                                                </option>
                                            ))}
                                        </optgroup>
                                        <optgroup label="Solo informazioni">
                                            {SERVICES.slice(0, 6).map((s) => (
                                                <option key={s.id} value={s.name}>
                                                    {s.name}
                                                </option>
                                            ))}
                                        </optgroup>
                                    </select>
                                </Field>
                                <Field label="Obiettivo principale" className="sm:col-span-2">
                                    <select
                                        data-testid="contact-select-goal"
                                        value={form.goal}
                                        onChange={update("goal")}
                                        className="input"
                                    >
                                        <option value="">Seleziona...</option>
                                        {GOALS.map((g) => (
                                            <option key={g} value={g}>
                                                {g}
                                            </option>
                                        ))}
                                    </select>
                                </Field>
                                <Field label="Messaggio *" className="sm:col-span-2">
                                    <textarea
                                        required
                                        data-testid="contact-input-message"
                                        value={form.message}
                                        onChange={update("message")}
                                        placeholder="Raccontaci qualcosa di te: età, esperienza, obiettivi..."
                                        rows={5}
                                        className="input resize-none"
                                    />
                                </Field>
                            </div>

                            <div className="mt-6 p-4 rounded-lg bg-gold/5 border border-gold/20 flex items-start gap-3">
                                <MessageCircle
                                    size={18}
                                    className="text-gold flex-shrink-0 mt-0.5"
                                />
                                <p className="font-manrope text-xs text-zinc-300 leading-relaxed">
                                    <span className="text-gold font-semibold">Come funziona</span>{" "}
                                    — Cliccando "Invia su WhatsApp" si aprirà WhatsApp con il tuo
                                    messaggio già pronto. Premi semplicemente invio per
                                    confermare e Manuel ti risponderà personalmente.
                                </p>
                            </div>

                            <div className="mt-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                                <p className="font-manrope text-xs text-zinc-500 max-w-sm">
                                    I tuoi dati restano riservati e non saranno condivisi.
                                </p>

                                {/* Vero link <a> con target=_blank: nessun browser blocca */}
                                <a
                                    href={waLink}
                                    target="_blank"
                                    rel="noopener"
                                    onClick={handleClick}
                                    data-testid="contact-submit-button"
                                    className="inline-flex items-center justify-center gap-2 bg-gold text-ink font-oswald font-bold uppercase tracking-wider px-8 py-4 rounded hover:bg-gold-bright hover:shadow-[0_0_24px_rgba(212,175,55,0.4)] transition-all cursor-pointer"
                                >
                                    Invia su WhatsApp
                                    <ArrowRight size={18} />
                                </a>
                            </div>
                        </form>
                    </div>
                </div>
            </div>

            <style>{`
                .input {
                    width: 100%;
                    background: #141A29;
                    border: 1px solid rgba(255,255,255,0.08);
                    color: #fff;
                    padding: 12px 14px;
                    border-radius: 6px;
                    font-family: 'Manrope', sans-serif;
                    font-size: 15px;
                    transition: all 0.2s;
                }
                .input:focus {
                    outline: none;
                    border-color: #D4AF37;
                    box-shadow: 0 0 0 2px rgba(212,175,55,0.2);
                }
                .input::placeholder { color: #52525B; }
                select.input { appearance: none; background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%23D4AF37' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'><polyline points='6 9 12 15 18 9'></polyline></svg>"); background-repeat: no-repeat; background-position: right 14px center; padding-right: 40px; }
                option, optgroup { background: #141A29; color: #fff; }
            `}</style>
        </section>
    );
}

function Field({ label, children, className = "" }) {
    return (
        <label className={`block ${className}`}>
            <span className="font-manrope text-xs uppercase tracking-[0.2em] text-zinc-400 mb-2 block">
                {label}
            </span>
            {children}
        </label>
    );
}
